import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, quantitySchema, versionSchema } from './common.js'
import type { CompanyProfile } from './company.js'
import { type CreditNote, returnLineInputSchema } from './credit-notes.js'
import { type LoyaltyInvoiceInfo, loyaltyRedemptionInputSchema } from './loyalty.js'
import { money, toMoney } from './money.js'
import {
  type CustomerRef,
  type CustomerRefund,
  discountTypeSchema,
  lineUnitIdSchema,
  type PaymentMethod,
  type PaymentMethodRef,
  paymentMethodIdSchema,
  type SalesInvoice,
} from './sales.js'
import { lineSerialsSchema } from './serials.js'

/**
 * Point of sale (ADR-012). A till sale is one server command that posts a sales invoice and one
 * receipt per tender; returns are credit notes against the sale (or, with pos.return_unlinked, with
 * no sale behind them), refunded at once.
 */

const positive = (message = 'Enter an amount above zero') => moneySchema.refine((v) => Number(v) > 0, message)
const nonNegative = moneySchema.refine((v) => Number(v) >= 0, 'Cannot be negative')

// ── terminals ──────────────────────────────────────────────────────────────────

export const posTerminalInputSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .min(1)
    .regex(/^[A-Z0-9_-]{1,32}$/, 'Use up to 32 letters, digits, dashes or underscores'),
  name: z.string().trim().min(1).max(100),
  nameAr: optionalText(100),
  /** Stock for till sales is issued from here. */
  warehouseId: z.uuid(),
  /** The drawer: a CASH treasury account. */
  cashAccountId: z.uuid(),
  /** Card (and other non-cash) takings: a CARD_TERMINAL or BANK account. None: the till takes cash only. */
  cardAccountId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  active: z.boolean().default(true),
})
export type PosTerminalInput = z.input<typeof posTerminalInputSchema>
export const posTerminalUpdateSchema = posTerminalInputSchema.extend({ version: versionSchema })
export type PosTerminalUpdate = z.input<typeof posTerminalUpdateSchema>

export interface PosTerminal {
  id: string
  code: string
  name: string
  nameAr: string | null
  warehouse: { id: string; code: string; name: string; nameAr: string | null }
  cashAccount: { id: string; name: string; nameAr: string | null }
  cardAccount: { id: string; name: string; nameAr: string | null } | null
  /**
   * The drawer is also the office's cash: the default cash account of back-office documents (Settings ›
   * Payments), or back-office receipts, refunds, payments or expenses went through it (a company set up
   * before drawers were the till's own). "Give this till its own drawer" fixes it (BR-POS-004).
   */
  officeCash: boolean
  active: boolean
  openShifts: number
  version: number
}

/**
 * "Give this till its own drawer" (`POST /pos/terminals/:id/own-drawer`, BR-POS-004): a new cash account
 * "Till drawer <code>" becomes the till's drawer, and the cash physically in the drawer now
 * (`cashInDrawer`, possibly 0) is transferred to it from the old one. Only while no shift is open on the
 * till.
 */
export const posOwnDrawerSchema = z.object({
  version: versionSchema,
  cashInDrawer: nonNegative,
})
export type PosOwnDrawerInput = z.input<typeof posOwnDrawerSchema>

// ── shifts ─────────────────────────────────────────────────────────────────────

export const shiftOpenSchema = z.object({
  terminalId: z.uuid(),
  /** Cash in the drawer at the start (no journal entry: it is already in the drawer account). */
  openingFloat: nonNegative.default('0'),
})
export type ShiftOpenInput = z.input<typeof shiftOpenSchema>

/**
 * Omani rial notes and coins accepted in a count: the legacy notes plus the baisa coins
 * (50, 25, 10 and 5 baisa).
 */
export const CASH_DENOMINATIONS = [
  '50.000',
  '20.000',
  '10.000',
  '5.000',
  '1.000',
  '0.500',
  '0.250',
  '0.100',
  '0.050',
  '0.025',
  '0.010',
  '0.005',
] as const

/** Closing needs the counted drawer, as whole counts of whitelisted denominations. */
export const shiftCloseSchema = z.object({
  denominations: z
    .array(
      z.object({
        value: moneySchema
          .refine((v) => CASH_DENOMINATIONS.some((d) => money(d).equals(v)), 'Not an accepted note or coin')
          .transform((v) => toMoney(v)),
        count: z.number().int('Count whole notes and coins').min(0).max(1_000_000),
      }),
    )
    .min(1, 'Count the drawer before closing')
    .max(CASH_DENOMINATIONS.length)
    .refine((list) => new Set(list.map((d) => d.value)).size === list.length, {
      message: 'Enter each note or coin once',
    }),
  notes: optionalText(500),
})
export type ShiftCloseInput = z.input<typeof shiftCloseSchema>

/** Σ value × count, rounded to the baisa. */
export function countedCash(denominations: { value: string; count: number }[]): string {
  return toMoney(denominations.reduce((s, d) => s.plus(money(d.value).times(d.count)), money(0)))
}

export const shiftListQuerySchema = pageQuerySchema.extend({
  status: z.enum(['OPEN', 'CLOSED']).optional(),
  terminalId: z.uuid().optional(),
  userId: z.uuid().optional(),
})
export type ShiftListQuery = z.input<typeof shiftListQuerySchema>

/** `GET /pos/shifts/summary`: the list's filters other than `status`, paging and sort. */
export const shiftSummaryQuerySchema = shiftListQuerySchema.pick({ terminalId: true, userId: true })
export type ShiftSummaryQuery = z.input<typeof shiftSummaryQuerySchema>

/** One month of till takings and drawer counts. */
export interface ShiftSummaryMonth {
  /** YYYY-MM */
  month: string
  /** Completed till sales (by invoice date) less posted till returns (by credit note date). */
  netSales: string
  /** Σ surplus of the shifts closed that month (counted above expected). */
  over: string
  /** Σ shortage of the shifts closed that month, as a positive amount. */
  short: string
  /** Shifts closed that month with a shortage. */
  shortCount: number
}

/**
 * The shifts the caller may list (everyone's with pos.view_shift or pos.manage_shifts, otherwise their
 * own), for the current filters: counts per status (each equals the `total` of the list call with that
 * status), the floats of the open ones, and the last six calendar months (oldest first, this one last).
 * Without pos.view_shift or pos.manage_shifts, only closed shifts' sales count (an open shift's takings
 * are the blind close's secret). As of `asOf`, today in Asia/Muscat.
 */
export interface ShiftSummary {
  asOf: string
  all: { count: number }
  open: { count: number; float: string }
  closed: { count: number }
  months: ShiftSummaryMonth[]
}

export interface PosShift {
  id: string
  /** SH-2026-00001 */
  number: string
  status: 'OPEN' | 'CLOSED'
  terminal: { id: string; code: string; name: string; nameAr: string | null }
  user: { id: string; name: string }
  openedAt: string
  openingFloat: string
  closedAt: string | null
  closedBy: { id: string; name: string } | null
  /** Hidden (null) from a cashier without pos.view_shift while the shift is open: blind close. */
  expectedCash: string | null
  countedCash: string | null
  /** counted − expected: positive is a surplus, negative a shortage. */
  variance: string | null
  notes: string | null
}

export interface PosCashMovement {
  id: string
  direction: 'IN' | 'OUT'
  amount: string
  reason: string
  /** The safe or bank on the other side of the transfer. */
  account: { id: string; name: string; nameAr: string | null }
  transfer: { id: string; number: string; status: 'POSTED' | 'VOID' }
  createdAt: string
}

/** X report (open shift, live) or Z report (closed shift, the snapshot taken at close). */
export interface PosShiftReport {
  kind: 'X' | 'Z'
  shift: PosShift
  /** Completed (not voided) sales. */
  sales: { count: number; total: string; discount: string; tax: string }
  voided: { count: number; total: string }
  /**
   * Tenders taken on completed sales, by type: `other` covers every other type, cheques included (as the
   * POS summary report counts them), so the four add up to the takings.
   */
  tenders: { cash: string; card: string; bankTransfer: string; other: string }
  /**
   * The same tenders by what took them: each type, and each company method apart (Visa, Thawani; ADR-047),
   * largest first. Optional only for older fixtures.
   */
  tenderMethods?: {
    method: PaymentMethod
    paymentMethod: PaymentMethodRef | null
    count: number
    amount: string
  }[]
  returns: { count: number; total: string; cashRefunds: string; cardRefunds: string; storeCredit: string }
  payIns: string
  payOuts: string
  openingFloat: string
  /** opening float + cash tenders − cash refunds + pay-ins − pay-outs. */
  expectedCash: string
  countedCash: string | null
  variance: string | null
  denominations: { value: string; count: number; amount: string }[] | null
  cashMovements: PosCashMovement[]
  generatedAt: string
}

// ── cash movements ─────────────────────────────────────────────────────────────

/** Pay-in (float top-up) and pay-out (bank drop): treasury transfers between the drawer and a safe or bank. */
export const cashMovementInputSchema = z.object({
  direction: z.enum(['IN', 'OUT']),
  /** The other side: an active treasury account other than the drawer. */
  accountId: z.uuid(),
  amount: positive(),
  reason: z.string().trim().min(1, 'Give a reason').max(300),
})
export type CashMovementInput = z.input<typeof cashMovementInputSchema>

// ── sales ──────────────────────────────────────────────────────────────────────

export const posLineInputSchema = z.object({
  variantId: z.uuid(),
  quantity: quantitySchema.refine((v) => Number(v) > 0, 'Quantity must be above zero'),
  /** A pack of the product (a scanned carton) or its base unit (default). */
  unitId: lineUnitIdSchema,
  /**
   * Default: the customer's price for the unit (custom price, else their level, ADR-045), plus VAT when
   * prices are entered tax-inclusive. A different price needs pos.price_override.
   */
  unitPrice: moneySchema.refine((v) => Number(v) >= 0, 'Price cannot be negative').optional(),
  discountType: discountTypeSchema.optional(),
  discountValue: moneySchema.refine((v) => Number(v) >= 0, 'Discount cannot be negative').optional(),
  /** Omitted: the product's tax rate. null: no tax. */
  taxRateId: z.uuid().nullable().optional(),
  /** Serial-tracked products: the serials sold (scanned or picked), one per unit. */
  serials: lineSerialsSchema,
})
export type PosLineInput = z.input<typeof posLineInputSchema>

/** A discount on the whole sale, spread over the lines before tax (see allocateDocumentDiscount). */
export const documentDiscountSchema = z
  .object({
    type: z.enum(['PERCENTAGE', 'AMOUNT']),
    value: positive('Enter a discount above zero'),
  })
  .refine((d) => d.type !== 'PERCENTAGE' || Number(d.value) <= 100, {
    path: ['value'],
    message: 'A percentage cannot exceed 100',
  })
export type DocumentDiscount = z.output<typeof documentDiscountSchema>

/**
 * A till has a cash drawer and, optionally, one card account, so it takes cash and card only. A bank
 * transfer or wallet payment for a till sale is a receipt into its own account, not a tender.
 */
export const TENDER_METHODS = ['CASH', 'CARD'] as const
export const tenderMethodSchema = z.enum(TENDER_METHODS)
export type TenderMethod = z.infer<typeof tenderMethodSchema>

export const tenderInputSchema = z
  .object({
    method: tenderMethodSchema,
    /**
     * A non-cash tender (`CARD`) by one of the company's methods offered at the till (Visa, Thawani;
     * ADR-047): the receipt records the method and its type, into its own account or the till's card account.
     */
    paymentMethodId: paymentMethodIdSchema,
    /** The amount applied to the sale; for cash, net of change. */
    amount: positive(),
    reference: optionalText(100),
  })
  .refine((t) => t.method === 'CARD' || t.paymentMethodId === null, {
    path: ['paymentMethodId'],
    message: 'Cash goes into the drawer',
  })

export const posSaleInputSchema = z
  .object({
    shiftId: z.uuid(),
    /**
     * The till's key for this cart, made once per cart and sent again on a retry: a sale already made
     * with it is returned as it is (no second invoice, stock issue or tender). The web always sends it.
     */
    clientRef: z
      .uuid()
      .transform((v) => v.toLowerCase())
      .nullish()
      .transform((v) => v ?? null),
    /** Default: the walk-in customer. */
    customerId: z
      .uuid()
      .nullish()
      .transform((v) => v ?? null),
    /** The salesperson credited (ADR-049): omitted = the customer's, else the shift's cashier; null = none. */
    salespersonId: z.uuid().nullish(),
    lines: z.array(posLineInputSchema).min(1, 'Add at least one item').max(500),
    discount: documentDiscountSchema.nullish().transform((v) => v ?? null),
    /**
     * Loyalty points the customer pays with (ADR-044): a discount after the other discounts and before VAT,
     * spread over the lines. Needs loyalty.redeem; refused with 409 LOYALTY_REDEMPTION_CHANGED when the
     * balance, the cap or the point value differ from what the till showed.
     */
    loyaltyRedemption: loyaltyRedemptionInputSchema.nullish().transform((v) => v ?? null),
    /**
     * They must add up to the sale total exactly (none for a sale worth nothing), unless `chargeToAccount`.
     */
    tenders: z.array(tenderInputSchema).max(10).default([]),
    /**
     * Charge what the tenders leave unpaid to the customer's account (G23): the rest stays open on the
     * invoice. Only for a customer who may buy on account at the till (`CustomerOption.tillCredit`);
     * anyone else, the walk-in customer included, is refused with 422 CREDIT_NOT_ALLOWED. While the Sales
     * module (which collects the rest) is off, it is refused with 422 TILL_CREDIT_NEEDS_SALES. The tenders
     * may then add up to anything from zero to the total.
     */
    chargeToAccount: z.boolean().default(false),
    /** Cash handed over; the change is this minus the cash tender. Default: the cash tender. */
    cashTendered: moneySchema
      .refine((v) => Number(v) >= 0, 'Cannot be negative')
      .nullish()
      .transform((v) => v ?? null),
    reference: optionalText(100),
    notes: optionalText(500),
  })
  .superRefine((v, ctx) => {
    const cash = v.tenders.filter((t) => t.method === 'CASH')
    if (cash.length > 1) {
      ctx.addIssue({ code: 'custom', path: ['tenders'], message: 'Enter cash once' })
    }
    if (!cash.length && v.cashTendered !== null && !money(v.cashTendered).isZero()) {
      ctx.addIssue({ code: 'custom', path: ['cashTendered'], message: 'There is no cash tender' })
    }
    if (cash[0] && v.cashTendered !== null && money(v.cashTendered).lessThan(cash[0].amount)) {
      ctx.addIssue({
        code: 'custom',
        path: ['cashTendered'],
        message: 'The cash handed over is less than the cash tender',
      })
    }
  })
export type PosSaleInput = z.input<typeof posSaleInputSchema>

export const posSaleListQuerySchema = pageQuerySchema.extend({
  shiftId: z.uuid().optional(),
  status: z.enum(['COMPLETED', 'VOID']).optional(),
  /** The sale whose invoice has exactly this number (case-insensitive): the till return lookup. */
  invoiceNumber: z.string().trim().min(1).max(50).optional(),
})
export type PosSaleListQuery = z.input<typeof posSaleListQuerySchema>

export interface PosSaleTender {
  receiptId: string
  receiptNumber: string
  method: PaymentMethod
  /** The company's own method (ADR-047), or null. Optional only for older fixtures. */
  paymentMethod?: PaymentMethodRef | null
  amount: string
  reference: string | null
  status: 'POSTED' | 'VOID'
}

export interface PosSaleListItem {
  id: string
  status: 'COMPLETED' | 'VOID'
  /** Sales are voided only while their shift is OPEN. */
  shift: { id: string; number: string; status: 'OPEN' | 'CLOSED' }
  invoice: { id: string; number: string | null }
  customer: CustomerRef
  total: string
  createdAt: string
  /**
   * Whether the caller may void this sale now: they hold pos.void, it is COMPLETED, nothing was
   * returned against it, its shift is OPEN, and it is their shift (or they hold pos.manage_shifts).
   */
  canVoid: boolean
}

/** Everything a till receipt prints. */
export interface PosSale extends PosSaleListItem {
  terminal: { id: string; code: string; name: string; nameAr: string | null }
  cashier: { id: string; name: string }
  customer: CustomerRef & { isWalkIn: boolean }
  /** The posted invoice: number, date, lines with discounts and tax, totals. */
  invoice: SalesInvoice
  taxByRate: { rate: string; net: string; tax: string }[]
  tenders: PosSaleTender[]
  /**
   * What the sale's returns can still pay back by tender (BR-POS-012): `card` to the card it was paid by
   * (its posted card tenders less the card refunds of its returns), `cash` from the drawer (its cash tenders
   * applied less the cash refunds of its returns), never below zero. A card refund above `card` is refused;
   * a cash refund above `cash` needs pos.refund_any_tender.
   */
  refundable: { card: string; cash: string }
  cashTendered: string
  changeGiven: string
  /**
   * The part of the total not paid at the sale, charged to the customer's account (G23); what is still due
   * now is `invoice.balance`. Always sent by the API; optional for older fixtures.
   */
  onAccount?: string
  seller: Omit<CompanyProfile, 'version'>
  voidReason: string | null
  voidedAt: string | null
  /** Points earned and redeemed on the sale (null: no loyalty on it, or the module is off). */
  loyalty: LoyaltyInvoiceInfo | null
}

// ── returns ────────────────────────────────────────────────────────────────────

/**
 * A return against a till sale: a RETURN credit note (quantities capped, original prices and cost),
 * refunded at once from the caller's drawer (cash) or to the sale's card account. Only a named
 * customer may keep the credit instead (STORE_CREDIT). The refund goes back the way the sale was paid
 * (`PosSale.refundable`): to the card at most what was paid by card, in cash at most what was paid in
 * cash unless the caller holds pos.refund_any_tender.
 */
export const posReturnInputSchema = z.object({
  shiftId: z.uuid(),
  saleId: z.uuid(),
  lines: z.array(returnLineInputSchema).min(1, 'Add at least one line').max(500),
  refundMethod: z.enum(['CASH', 'CARD', 'STORE_CREDIT']),
  reason: z.string().trim().min(1, 'Give a reason').max(500),
})
export type PosReturnInput = z.input<typeof posReturnInputSchema>

/**
 * A line of an unlinked return: a product coming back with no till sale behind it. The price and tax
 * rate default to the product's current ones (the price as the till would sell it today); a price
 * above that, or a tax rate higher than the product's, needs pos.price_override.
 */
export const posUnlinkedReturnLineInputSchema = z.object({
  variantId: z.uuid(),
  quantity: quantitySchema.refine((v) => Number(v) > 0, 'Quantity must be above zero'),
  /** A pack of the product or its base unit (default). */
  unitId: lineUnitIdSchema,
  /** Default: the current selling price (plus VAT when prices are entered tax-inclusive). */
  unitPrice: moneySchema.refine((v) => Number(v) >= 0, 'Price cannot be negative').optional(),
  /** Omitted: the product's tax rate. null: no tax. */
  taxRateId: z.uuid().nullable().optional(),
  /** Serial-tracked products: one per unit, each either unknown or REMOVED (never IN_STOCK or SOLD). */
  serials: lineSerialsSchema,
})
export type PosUnlinkedReturnLineInput = z.input<typeof posUnlinkedReturnLineInputSchema>

/**
 * A till return that references no till sale (pos.return_unlinked; ADR-012 addendum): a RETURN credit
 * note flagged unlinked, stock back at the current average cost, refunded like a linked return.
 */
export const posUnlinkedReturnInputSchema = z.object({
  shiftId: z.uuid(),
  /** Default: the walk-in customer (refunded at once, never store credit). */
  customerId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  lines: z.array(posUnlinkedReturnLineInputSchema).min(1, 'Add at least one line').max(500),
  refundMethod: z.enum(['CASH', 'CARD', 'STORE_CREDIT']),
  reason: z.string().trim().min(1, 'Give a reason').max(500),
})
export type PosUnlinkedReturnInput = z.input<typeof posUnlinkedReturnInputSchema>

/** Everything an 80 mm return receipt prints (as PosSale does for a sale). */
export interface PosReturn {
  id: string
  shift: { id: string; number: string }
  terminal: { id: string; code: string; name: string; nameAr: string | null }
  /** Who took the return (the shift's cashier). */
  cashier: { id: string; name: string }
  customer: CustomerRef & { isWalkIn: boolean }
  /** The till sale returned against; null for an unlinked return. */
  sale: { id: string; invoiceNumber: string | null } | null
  /** True: no till sale behind it (pos.return_unlinked). */
  unlinked: boolean
  reason: string
  method: 'CASH' | 'CARD' | 'STORE_CREDIT'
  /** The credit note total (VAT-inclusive), as a positive amount. */
  amount: string
  /** What was paid out (the refund's amount); 0 for store credit. */
  refunded: string
  /** Net and VAT per rate, from the credit note's lines. */
  taxByRate: { rate: string; net: string; tax: string }[]
  /** The posted credit note: number (CN-…), date, lines, totals. */
  creditNote: CreditNote
  /** The refund (REF-…): number, method, amount. Null for store credit. */
  refund: CustomerRefund | null
  seller: Omit<CompanyProfile, 'version'>
  createdAt: string
  /**
   * What loyalty did on the sale's invoice after this return (points redeemed on it, given back on its
   * returns, what it still earns, the member's balance); null for a return with no sale, a sale with no
   * loyalty on it, or the module off.
   */
  loyalty: LoyaltyInvoiceInfo | null
}

/** A shift's till returns, newest first (to open and reprint their receipts). */
export const posReturnListQuerySchema = pageQuerySchema.extend({ shiftId: z.uuid() })
export type PosReturnListQuery = z.input<typeof posReturnListQuerySchema>

export interface PosReturnListItem {
  id: string
  createdAt: string
  creditNote: { id: string; number: string | null }
  /** The till sale returned against; null for a return with no sale (unlinked). */
  sale: { id: string; invoiceNumber: string | null } | null
  customer: CustomerRef & { isWalkIn: boolean }
  method: 'CASH' | 'CARD' | 'STORE_CREDIT'
  /** The credit note total (VAT-inclusive), as a positive amount. */
  amount: string
}

// ── shared calculations (the till previews exactly what the API stores) ─────────

/**
 * The price a till line starts at. Catalogue prices exclude tax; when the tenant enters prices
 * tax-inclusive, the line's VAT is added so the customer pays the shelf price.
 */
export function sellingPrice(listPrice: string, taxRate: string, pricesIncludeTax: boolean): string {
  if (!pricesIncludeTax || money(taxRate).isZero()) return toMoney(listPrice)
  return toMoney(money(listPrice).times(money(1).plus(money(taxRate).dividedBy(100))))
}

/**
 * Spreads a document discount over lines in proportion to their amounts before tax (quantity ×
 * price on the entered basis). Shares are rounded cumulatively — share i = round(D × Σ₁..ᵢ base /
 * Σ base) − round(D × Σ₁..ᵢ₋₁ base / Σ base) — so they add up to the discount exactly.
 * A percentage is taken of the lines' total; an amount is used as given.
 */
export function allocateDocumentDiscount(
  bases: string[],
  discount: DocumentDiscount,
): { total: string; shares: string[] } {
  const totalBase = bases.reduce((s, b) => s.plus(b), money(0))
  const total =
    discount.type === 'PERCENTAGE'
      ? money(toMoney(totalBase.times(discount.value).dividedBy(100)))
      : money(toMoney(discount.value))
  if (!totalBase.greaterThan(0)) return { total: toMoney(total), shares: bases.map(() => '0.000') }
  let running = money(0)
  let previous = money(0)
  const shares = bases.map((b) => {
    running = running.plus(b)
    const cumulative = money(toMoney(total.times(running).dividedBy(totalBase)))
    const share = cumulative.minus(previous)
    previous = cumulative
    return toMoney(share)
  })
  return { total: toMoney(total), shares }
}
